import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { TrendingUp } from '@material-ui/icons';
import {
  creatorsAnalyticsInstagramFetch,
  creatorsAnalyticsYoutubeFetch,
  getBrandOverlapCount,
} from 'api_handlers/competitor';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    '& > *': {
      marginBottom: theme.spacing(4),
    },
  },
  sectionTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    fontSize: '0.75rem',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  legendBox: {
    width: 12,
    height: 12,
    borderRadius: 2,
  },
  networkContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  networkSvg: {
    width: '100%',
    maxWidth: 500,
    height: 250,
  },
  table: {
    borderCollapse: 'separate',
    borderSpacing: '4px',
    width: '100%',
    fontSize: '0.75rem',
    '& .MuiTableCell-root': {
      padding: theme.spacing(1),
      borderBottom: 'none',
      borderRadius: 4,
      textAlign: 'center',
      fontSize: '0.75rem',
    },
    '& .MuiTableCell-head': {
      fontWeight: 600,
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
    },
  },
  brandCell: {
    fontWeight: 600,
    textAlign: 'left !important',
  },
  exclusiveCell: {
    fontWeight: 600,
    fontSize: '0.625rem',
    padding: '4px 8px',
    borderRadius: 4,
    display: 'inline-block',
  },
  overlapCell: {
    fontSize: '0.625rem',
    padding: '4px 8px',
    borderRadius: 4,
    display: 'inline-block',
  },
  diagonalCell: {
    color: theme.palette.text.disabled,
  },
  note: {
    fontSize: '0.625rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    marginTop: theme.spacing(1),
    textAlign: 'center',
  },
  insightBox: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  insightHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  insightText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
}));

const BRAND_COLORS = ['#f9a620', '#ffd449', '#aab03c', '#548c2f', '#000000'];

const getBrandColor = (brandName, index) => {
  return BRAND_COLORS[index % BRAND_COLORS.length];
};

export default function InfluencerOverlapSection({
  selectedBrands = [],
  influencerOverlapData = [],
  influencersData = [],
  showAsPercentage = false,
  summary = {},
  competitorBrands = [],
  start_date = null,
  end_date = null,
  platform = 'instagram',
}) {
  const classes = useStyles();
  const [overlapApiData, setOverlapApiData] = useState({}); // Store overlap data from API
  const [analyticsData, setAnalyticsData] = useState({}); // Store analytics per brand

  // Fetch brand overlap data from API
  const fetchBrandOverlap = useCallback(async () => {
    if (selectedBrands.length === 0 || competitorBrands.length === 0) return;

    const overlapMap = {};

    // Fetch overlap for each brand
    for (const brandTitle of selectedBrands) {
      const brandData = competitorBrands.find((b) => b.title === brandTitle);
      if (!brandData) continue;

      const isValidYtHandle = (h) =>
        h && h.trim() !== '' && !h.toLowerCase().includes('test');

      // Get the brand handle (prefer ig_handle, fallback to valid yt_handle or title)
      const brandHandle = platform === 'instagram'
        ? brandData.ig_handle
        : isValidYtHandle(brandData.yt_handle)
          ? brandData.yt_handle
          : brandData.handle || brandTitle;

      if (platform !== 'instagram' && !isValidYtHandle(brandData.yt_handle) && !brandData.handle) {
        overlapMap[brandTitle] = { total: 0, overlaps: {} };
        continue;
      }

      // Get other brands (comma-separated handles)
      const otherBrands = selectedBrands
        .filter((b) => b !== brandTitle)
        .map((b) => {
          const otherBrandData = competitorBrands.find((comp) => comp.title === b);
          if (platform === 'instagram') return otherBrandData?.ig_handle;
          return isValidYtHandle(otherBrandData?.yt_handle)
            ? otherBrandData?.yt_handle
            : otherBrandData?.handle || b;
        })
        .filter(Boolean)
        .join(',');

      if (otherBrands) {
        try {
          const overlapResponse = await getBrandOverlapCount(
            brandHandle,
            start_date || '',
            end_date || '',
            '',
            otherBrands,
            platform || 'instagram'
          );

          if (overlapResponse && Array.isArray(overlapResponse)) {
            // Helper to normalize strings for comparison
            const normalize = (str) => (str || '').toString().toLowerCase().trim();
            
            // Find the entry for this brand with flexible matching
            const brandEntry = overlapResponse.find((entry) => {
              const entryHandle = normalize(entry.brand_handle || '');
              const entryName = normalize(entry.brand_name || '');
              const searchHandle = normalize(brandHandle);
              const searchTitle = normalize(brandTitle);
              
              return (
                entryHandle === searchHandle ||
                entryHandle === searchTitle ||
                entryName === searchHandle ||
                entryName === searchTitle ||
                entry.brand_handle === brandHandle ||
                entry.brand_name === brandTitle
              );
            });
            
            if (brandEntry) {
              // Extract overlap counts (all keys except brand_handle, brand_name, total)
              const overlapCounts = {};
              Object.keys(brandEntry).forEach((key) => {
                if (key !== 'brand_handle' && key !== 'brand_name' && key !== 'total') {
                  overlapCounts[key] = brandEntry[key] || 0;
                }
              });
              
              overlapMap[brandTitle] = {
                total: brandEntry.total || 0,
                overlaps: overlapCounts, // Store overlap counts keyed by other brand handles
              };
            } else {
              // Log for debugging if entry not found
              console.warn(`Brand entry not found for ${brandTitle} (handle: ${brandHandle})`, {
                availableEntries: overlapResponse.map(e => ({ 
                  handle: e.brand_handle, 
                  name: e.brand_name 
                }))
              });
            }
          }
        } catch (error) {
          console.error(`Error fetching overlap for ${brandTitle}:`, error);
          overlapMap[brandTitle] = {
            total: 0,
            overlaps: {},
          };
        }
      } else {
        // Single brand, no overlaps
        overlapMap[brandTitle] = {
          total: 0,
          overlaps: {},
        };
      }
    }

    setOverlapApiData(overlapMap);
  }, [selectedBrands, competitorBrands, start_date, end_date, platform]);

  // Fetch analytics data for all selected brands
  const fetchAnalytics = useCallback(async () => {
    if (selectedBrands.length === 0 || competitorBrands.length === 0) return;

    const analyticsMap = {};

    // Fetch analytics for each brand
    for (const brandTitle of selectedBrands) {
      const brandData = competitorBrands.find((b) => b.title === brandTitle);
      if (!brandData) continue;

      try {
        let instagramAnalytics = null;
        let youtubeAnalytics = null;
        let totalInfluencers = 0;

        // Fetch Instagram analytics
        if (brandData.ig_handle && brandData.ig_handle.trim() !== '') {
          instagramAnalytics = await creatorsAnalyticsInstagramFetch(
            brandData.ig_handle,
            start_date || '',
            end_date || '',
            '',
            false,
            null,
            brandData.excluded_handles
          );

          if (instagramAnalytics?.summary) {
            totalInfluencers += instagramAnalytics.summary.total_influencers || 0;
          }
        }

        // Fetch YouTube analytics
        if (brandData.yt_handle && brandData.yt_handle.trim() !== '' && !brandData.yt_handle.toLowerCase().includes('test')) {
          youtubeAnalytics = await creatorsAnalyticsYoutubeFetch(
            brandData.yt_handle,
            start_date || '',
            end_date || '',
            '',
            false,
            null,
            brandData.excluded_channel_ids
          );

          if (youtubeAnalytics?.summary) {
            totalInfluencers += youtubeAnalytics.summary.total_influencers || 0;
          }
        }

        analyticsMap[brandTitle] = {
          total_influencers: totalInfluencers,
        };
      } catch (error) {
        console.error(`Error fetching analytics for ${brandTitle}:`, error);
        analyticsMap[brandTitle] = {
          total_influencers: 0,
        };
      }
    }

    setAnalyticsData(analyticsMap);
  }, [selectedBrands, competitorBrands, start_date, end_date]);

  useEffect(() => {
    fetchBrandOverlap();
    fetchAnalytics();
  }, [fetchBrandOverlap, fetchAnalytics]);

  // Calculate overlap matrix from API data
  const overlapMatrix = useMemo(() => {
    if (selectedBrands.length === 0) return [];

    // Helper function to get brand handle from title
    const getBrandHandle = (brandTitle) => {
      const brandData = competitorBrands.find((b) => b.title === brandTitle);
      return brandData?.ig_handle || brandData?.yt_handle || brandData?.handle || brandTitle;
    };

    // Helper function to normalize strings for comparison (case-insensitive, trim)
    const normalizeString = (str) => {
      return (str || '').toString().toLowerCase().trim();
    };

    // Helper function to find overlap count with flexible matching
    const findOverlapCount = (overlapData, otherBrandTitle) => {
      if (!overlapData || !otherBrandTitle) return 0;

      // Get all possible identifiers for the other brand
      const otherBrandData = competitorBrands.find((b) => b.title === otherBrandTitle);
      const possibleKeys = [
        otherBrandTitle,
        otherBrandData?.ig_handle,
        otherBrandData?.yt_handle,
        otherBrandData?.handle,
        getBrandHandle(otherBrandTitle),
      ].filter(Boolean); // Remove undefined/null values

      // Try exact match first
      for (const key of possibleKeys) {
        if (overlapData.hasOwnProperty(key)) {
          return overlapData[key] || 0;
        }
      }

      // Try case-insensitive match
      const normalizedOtherBrand = normalizeString(otherBrandTitle);
      for (const [key, value] of Object.entries(overlapData)) {
        const normalizedKey = normalizeString(key);
        if (normalizedKey === normalizedOtherBrand) {
          return value || 0;
        }
      }

      // Try matching against all possible normalized keys
      const normalizedPossibleKeys = possibleKeys.map(normalizeString);
      for (const [key, value] of Object.entries(overlapData)) {
        const normalizedKey = normalizeString(key);
        if (normalizedPossibleKeys.includes(normalizedKey)) {
          return value || 0;
        }
      }

      return 0;
    };

    return selectedBrands.map((brand, brandIndex) => {
      // Use total from overlap API, fallback to analytics or summary
      const overlapInfo = overlapApiData[brand];
      const total = overlapInfo?.total || analyticsData[brand]?.total_influencers || summary[brand]?.creators || 0;
      
      // Get overlaps from API response
      const overlaps = {};
      let totalShared = 0;
      
      if (overlapInfo?.overlaps && Object.keys(overlapInfo.overlaps).length > 0) {
        selectedBrands.forEach((otherBrand) => {
          if (otherBrand === brand) {
            overlaps[otherBrand] = null; // Diagonal
          } else {
            // Find overlap count using flexible matching
            const overlapCount = findOverlapCount(overlapInfo.overlaps, otherBrand);
            overlaps[otherBrand] = overlapCount;
            totalShared += overlapCount;
          }
        });
      } else {
        // Fallback: if API data not available, set all overlaps to 0
        selectedBrands.forEach((otherBrand) => {
          if (otherBrand === brand) {
            overlaps[otherBrand] = null; // Diagonal
          } else {
            overlaps[otherBrand] = 0;
          }
        });
      }

      // Calculate exclusive and shared
      // Exclusive = total - sum of all overlaps
      // Shared = sum of all overlaps
      const shared = totalShared;
      const exclusive = Math.max(0, total - shared);

      return {
        brand,
        total,
        exclusive,
        shared,
        overlaps,
        brandIndex,
      };
    });
  }, [selectedBrands, overlapApiData, analyticsData, summary, competitorBrands]);

  // Bar chart data - horizontal bars with brands on y-axis

  // Network visualization positions (pentagon layout)
  const getNetworkPositions = () => {
    const positions = {};
    const centerX = 200;
    const centerY = 150;
    const radius = 80;
    
    selectedBrands.forEach((brand, index) => {
      const angle = (2 * Math.PI * index) / selectedBrands.length - Math.PI / 2;
      positions[brand] = {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
      };
    });
    
    return positions;
  };

  // Generate insight
  const generateInsight = () => {
    if (overlapMatrix.length === 0) return 'No data available.';

    const withRates = overlapMatrix.map((row) => ({
      ...row,
      exclusivityRate: row.total > 0 ? ((row.exclusive / row.total) * 100) : 0,
    }));

    const bestExclusivity = withRates.reduce((max, item) =>
      item.exclusivityRate > max.exclusivityRate ? item : max
    );

    const mostExclusive = withRates.reduce((max, item) =>
      item.exclusive > max.exclusive ? item : max
    );

    if (bestExclusivity.brand === mostExclusive.brand) {
      return `${bestExclusivity.brand} maintains strongest exclusivity with ${bestExclusivity.exclusive} exclusive vs ${bestExclusivity.shared} shared influencers (${bestExclusivity.exclusivityRate.toFixed(0)}% exclusive rate), demonstrating strong brand partnerships.`;
    }

    return `${bestExclusivity.brand} leads in exclusivity rate at ${bestExclusivity.exclusivityRate.toFixed(0)}% (${bestExclusivity.exclusive}/${bestExclusivity.total}). ${mostExclusive.brand} has most exclusive partnerships with ${mostExclusive.exclusive} exclusive influencers (${mostExclusive.exclusivityRate.toFixed(0)}% rate).`;
  };

  const networkPositions = getNetworkPositions();

  return (
    <Box className={classes.container}>
      {/* Influencer Distribution Chart - Full Width
      <Box>
        <Typography className={classes.sectionTitle}>Influencer Distribution</Typography>
        <Chart
          options={barChartData.options}
          series={barChartData.series}
          type="bar"
          height={120}
        />
        <Box className={classes.legend}>
          <Box className={classes.legendItem}>
            <Box
              className={classes.legendBox}
              style={{ backgroundColor: getBrandColor('', 0) }}
            />
            <Typography variant="caption" style={{ color: '#9ca3af' }}>
              Exclusive
            </Typography>
          </Box>
          <Box className={classes.legendItem}>
            <Box
              className={classes.legendBox}
              style={{ backgroundColor: getBrandColor('', 1) }}
            />
            <Typography variant="caption" style={{ color: '#9ca3af' }}>
              Shared
            </Typography>
          </Box>
        </Box>
      </Box> */}

      {/* Brand Overlap Visualization */}
      <Box>
        <Typography className={classes.sectionTitle}>Brand Overlap Visualization</Typography>
        <Box className={classes.networkContainer}>
          <svg viewBox="0 0 400 300" className={classes.networkSvg}>
            {/* Draw connections */}
            {selectedBrands.map((brand, i) =>
              selectedBrands.slice(i + 1).map((otherBrand) => {
                const overlap = overlapMatrix.find(m => m.brand === brand)?.overlaps[otherBrand] || 0;
                if (overlap === 0 || overlap === null) return null;

                const pos1 = networkPositions[brand];
                const pos2 = networkPositions[otherBrand];
                if (!pos1 || !pos2) return null;

                const strokeWidth = Math.max(2, Math.min(overlap / 3, 8));

                return (
                  <g key={`${brand}-${otherBrand}`}>
                    <line
                      x1={pos1.x}
                      y1={pos1.y}
                      x2={pos2.x}
                      y2={pos2.y}
                      stroke={getBrandColor(brand, overlapMatrix.find(m => m.brand === brand)?.brandIndex || 0)}
                      strokeWidth={strokeWidth}
                      opacity={0.4}
                    />
                    <text
                      x={(pos1.x + pos2.x) / 2}
                      y={(pos1.y + pos2.y) / 2}
                      fill="currentColor"
                      fontSize="12"
                      fontWeight="bold"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{ color: '#9ca3af' }}
                    >
                      {overlap}
                    </text>
                  </g>
                );
              })
            )}

            {/* Draw brand circles */}
            {overlapMatrix.map((row) => {
              const pos = networkPositions[row.brand];
              if (!pos) return null;
              const brandColor = getBrandColor(row.brand, row.brandIndex);

              return (
                <g key={row.brand}>
                  <circle
                    cx={pos.x}
                    cy={pos.y}
                    r={35}
                    fill={brandColor}
                    opacity={0.2}
                    stroke={brandColor}
                    strokeWidth={2}
                  />
                  <text
                    x={pos.x}
                    y={pos.y - 5}
                    fill={brandColor}
                    fontSize="12"
                    fontWeight="bold"
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    {row.brand}
                  </text>
                  <text
                    x={pos.x}
                    y={pos.y + 10}
                    fill="currentColor"
                    fontSize="11"
                    fontWeight="600"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{ color: '#9ca3af' }}
                  >
                    {row.total}
                  </text>
                </g>
              );
            })}
          </svg>
        </Box>
        <Typography className={classes.note}>
          * Circle numbers show total influencers, connection numbers show shared influencers
        </Typography>
      </Box>

      {/* Brand Overlap Matrix */}
      <Box>
        <Typography className={classes.sectionTitle}>Brand Overlap Matrix</Typography>
        <TableContainer component={Paper} style={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.brandCell}>Brand</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Exclusive</TableCell>
                {selectedBrands.map((brand, index) => (
                  <TableCell
                    key={brand}
                    style={{ color: getBrandColor(brand, index), fontSize: '0.75rem' }}
                  >
                    {brand}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {overlapMatrix.map((row, rowIndex) => {
                const brandColor = getBrandColor(row.brand, row.brandIndex);
                return (
                  <TableRow key={row.brand}>
                    <TableCell className={classes.brandCell} style={{ color: brandColor }}>
                      {row.brand}
                    </TableCell>
                    <TableCell style={{ fontWeight: 700 }}>{row.total}</TableCell>
                    <TableCell>
                      <Box
                        className={classes.exclusiveCell}
                        style={{
                          backgroundColor: `${brandColor}20`,
                          color: brandColor,
                        }}
                      >
                        {row.exclusive}
                      </Box>
                    </TableCell>
                    {selectedBrands.map((otherBrand, otherIndex) => {
                      const overlap = row.overlaps[otherBrand];
                      const otherBrandColor = getBrandColor(otherBrand, otherIndex);
                      
                      if (overlap === null) {
                        return (
                          <TableCell key={otherBrand} className={classes.diagonalCell}>
                            -
                          </TableCell>
                        );
                      }
                      
                      return (
                        <TableCell key={otherBrand}>
                          <Box
                            className={classes.overlapCell}
                            style={{
                              backgroundColor: overlap > 0 ? `${otherBrandColor}15` : 'transparent',
                              color: overlap > 0 ? otherBrandColor : '#9ca3af',
                              opacity: overlap > 0 ? 1 : 0.3,
                            }}
                          >
                            {overlap}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography className={classes.note}>
          * Overlap numbers show influencers working with both brands
        </Typography>
      </Box>

      {/* Key Insight */}
      <Box className={classes.insightBox}>
        <Box className={classes.insightHeader}>
          <TrendingUp style={{ color: '#10b981', fontSize: 16 }} />
          <Typography variant="subtitle2" style={{ fontWeight: 500 }}>
            Key Insight
          </Typography>
        </Box>
        <Typography className={classes.insightText}>
          {generateInsight()}
        </Typography>
      </Box>
    </Box>
  );
}
